import { api } from '@/lib/api';

export interface Admin {
  id: string;
  email: string;
  name: string;
  mobile?: string;
  profileImage?: string;
  role: string;
  status: 'active' | 'inactive';
  isSuperAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminData {
  email: string;
  name: string;
  mobile?: string;
  password: string;
}

export interface UpdateAdminData {
  email?: string;
  name?: string;
  mobile?: string;
  status?: 'active' | 'inactive';
}

export interface UpdateProfileData {
  name?: string;
  mobile?: string;
  removeImage?: boolean;
}

export interface UpdatePasswordData {
  currentPassword?: string;
  newPassword: string;
}

export interface DeleteTestingUserResponse {
  success: boolean;
  message?: string;
  driveFolderAttempted?: boolean;
  driveFolderDeleted?: boolean;
}

export const adminService = {
  async getAll(): Promise<Admin[]> {
    const response = await api.get<Admin[]>('/admin');
    return response.data;
  },

  async getOne(id: string): Promise<Admin> {
    const response = await api.get<Admin>(`/admin/${id}`);
    return response.data;
  },

  async create(data: CreateAdminData): Promise<Admin> {
    const response = await api.post<Admin>('/admin', data);
    return response.data;
  },

  async update(id: string, data: UpdateAdminData): Promise<Admin> {
    const response = await api.put<Admin>(`/admin/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/admin/${id}`);
  },

  async sendPasswordResetEmail(email: string): Promise<void> {
    await api.post('/admin/send-reset-email', { email });
  },

  async updateProfile(data: UpdateProfileData, profileImage?: File, removeImage?: boolean): Promise<Admin> {
    const formData = new FormData();
    formData.append('name', data.name || '');
    formData.append('mobile', data.mobile || '');
    if (profileImage) {
      formData.append('profileImage', profileImage);
    }
    if (removeImage) {
      formData.append('removeProfileImage', 'true');
    }
    
    const response = await api.put<Admin>('/admin/me/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async getMyProfile(): Promise<Admin> {
    const response = await api.get<Admin>('/admin/me');
    return response.data;
  },

  async updatePassword(data: UpdatePasswordData): Promise<Admin> {
    const response = await api.put<Admin>('/admin/me/password', data);
    return response.data;
  },

  // Testing – list/delete users (temporary)
  async getTestingUsers(): Promise<{ id: string; email: string; name: string | null; createdAt: string }[]> {
    const response = await api.get<{ id: string; email: string; name: string | null; createdAt: string }[]>('/testing/users');
    return response.data;
  },
  async deleteTestingUser(userId: string): Promise<DeleteTestingUserResponse> {
    const response = await api.delete<DeleteTestingUserResponse>(`/testing/users/${userId}`);
    return response.data;
  },
};

